import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  computed,
  input
} from '@angular/core';
import { UmssBadgeComponent } from '../badge/badge.component';
import {
  BackgroundCheckOutcomeEnum,
  BackgroundCheckStatusEnum
} from '@umss/shared/data-access';
import { BadgeVariant } from '@umss/shared/utils';

interface BackgroundCheckBadgeProps {
  label: string;
  iconName?: string;
  variant: BadgeVariant;
  outlined: boolean;
}

/**
 * Computes badge presentation from status + outcome + adjudicationStatus.
 * Precedence (highest first): adjudicationStatus → outcome → status.
 *
 * Exported so contractor, facility, and business components can reuse the
 * same mapping logic without duplicating it.
 */
export function getBackgroundCheckBadgeProps(
  status: BackgroundCheckStatusEnum | undefined | null,
  outcome?: BackgroundCheckOutcomeEnum | null,
  adjudicationStatus?: 'Engaged' | 'PreAdverseAction' | 'PostAdverseAction' | 'Disputed' | 'Approved' | 'Rejected' | null
): BackgroundCheckBadgeProps {
  if (adjudicationStatus) {
    switch (adjudicationStatus) {
      case 'Approved':
        return { label: 'Approved', iconName: 'phosphorCheckCircle', variant: 'success', outlined: false };
      case 'Rejected':
        return { label: 'Rejected', iconName: 'phosphorXCircle', variant: 'error', outlined: false };
      case 'Engaged':
        return { label: 'Eligible', iconName: 'phosphorCheckCircle', variant: 'success', outlined: false };
      case 'PreAdverseAction':
        return { label: 'In Review', iconName: 'phosphorWarning', variant: 'warning', outlined: false };
      case 'PostAdverseAction':
        return { label: 'Not Eligible', iconName: 'phosphorXCircle', variant: 'error', outlined: false };
      case 'Disputed':
        return { label: 'Disputed', iconName: 'phosphorWarning', variant: 'warning', outlined: false };
    }
  }

  // Suspended outranks status. The BE records Outcome=Suspended on report.suspended but
  // leaves Status=InProgress until a follow-up event resolves it (report.resumed, or
  // report.completed with status=suspended), so reading the outcome only under Completed
  // would render a suspended check as "Pending".
  if (outcome === BackgroundCheckOutcomeEnum.Suspended) {
    return { label: 'Suspended', iconName: 'phosphorWarning', variant: 'error', outlined: false };
  }

  if (!status) {
    return { label: 'Not Started', iconName: 'phosphorClock', variant: 'neutral', outlined: true };
  }

  switch (status) {
    case BackgroundCheckStatusEnum.Initiated:
      return { label: 'Initiated', iconName: 'phosphorClock', variant: 'warning', outlined: false };

    case BackgroundCheckStatusEnum.InProgress:
      return { label: 'Pending', iconName: 'phosphorSpinner', variant: 'warning', outlined: false };

    case BackgroundCheckStatusEnum.Completed:
      if (outcome === BackgroundCheckOutcomeEnum.Clear) {
        return { label: 'Eligible / Clear', iconName: 'phosphorCheckCircle', variant: 'success', outlined: false };
      }
      if (outcome === BackgroundCheckOutcomeEnum.Consider) {
        return { label: 'Pending / Under Review', iconName: 'phosphorWarning', variant: 'warning', outlined: false };
      }
      // Suspended is handled above the switch — it can arrive on a non-Completed check.
      return { label: 'Completed', iconName: 'phosphorCheckCircle', variant: 'neutral', outlined: true };

    case BackgroundCheckStatusEnum.Expired:
      return { label: 'Expired', iconName: 'phosphorClock', variant: 'neutral', outlined: true };

    case BackgroundCheckStatusEnum.Cancelled:
      return { label: 'Canceled', iconName: 'phosphorXCircle', variant: 'neutral', outlined: true };

    case BackgroundCheckStatusEnum.Error:
      return { label: 'Error', iconName: 'phosphorWarning', variant: 'error', outlined: false };

    case BackgroundCheckStatusEnum.PendingContractorConsent:
      return { label: 'Invitation Sent', iconName: 'phosphorClock', variant: 'neutral', outlined: true };

    default:
      return { label: 'Unknown', variant: 'neutral', outlined: true };
  }
}

/**
 * Facility-facing variant of the mapping.
 *
 * Facilities get progress information — has this professional started, are they mid-check,
 * are they cleared — but never the substance of a report: a `Consider` result and a suspended
 * report both collapse into one neutral "In review" chip.
 *
 * Why: report detail reaches a facility only through the consent-gated PDF
 * (`facilityReportConsentGiven`, the checkbox the contractor ticks before starting a check).
 * An unconditional badge saying "Under Review" would tell a facility that something surfaced
 * on a named professional without that professional having agreed to it — routing around the
 * consent gate. Adjudication stages are deliberately not a parameter here.
 */
export function getFacilityBackgroundCheckBadgeProps(
  status: BackgroundCheckStatusEnum | undefined | null,
  outcome?: BackgroundCheckOutcomeEnum | null
): BackgroundCheckBadgeProps {
  if (
    outcome === BackgroundCheckOutcomeEnum.Consider ||
    outcome === BackgroundCheckOutcomeEnum.Suspended
  ) {
    return { label: 'In review', iconName: 'phosphorClock', variant: 'neutral', outlined: true };
  }
  return getBackgroundCheckBadgeProps(status, outcome);
}

@Component({
  selector: 'umss-background-check-status-badge',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UmssBadgeComponent],
  template: `
    <umss-badge
      [variant]="props().variant"
      [label]="props().label"
      [iconName]="props().iconName"
      [outlined]="props().outlined"
      [size]="size()"
    />
  `
})
export class UmssBackgroundCheckStatusBadgeComponent {
  @HostBinding('style.display') display = 'contents';

  status = input<BackgroundCheckStatusEnum | undefined | null>();
  outcome = input<BackgroundCheckOutcomeEnum | null | undefined>();
  adjudicationStatus = input<'Engaged' | 'PreAdverseAction' | 'PostAdverseAction' | 'Disputed' | 'Approved' | 'Rejected' | null | undefined>();
  size = input<'small' | 'medium' | 'large'>('medium');

  /**
   * Who is looking. `facility` narrows the mapping so client facilities see progress but not
   * report substance — see getFacilityBackgroundCheckBadgeProps. It also ignores
   * adjudicationStatus outright, so FCRA stages can never leak to a facility by accident.
   */
  audience = input<'internal' | 'facility'>('internal');

  readonly props = computed(() =>
    this.audience() === 'facility'
      ? getFacilityBackgroundCheckBadgeProps(this.status(), this.outcome())
      : getBackgroundCheckBadgeProps(this.status(), this.outcome(), this.adjudicationStatus())
  );
}
